import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CurrencyEur, Plus, Trash, TrendUp, TrendDown, FileCsv, PencilSimple, GearSix, FilePdf, CaretDown } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link, useNavigate } from 'react-router-dom';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

// Arredonda somas monetárias a 2 casas para evitar erros de vírgula flutuante na exibição.
const r2 = (n) => (Math.round((Number(n) || 0) * 100) / 100);

export default function FinanceiroPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isViewer = user?.role === 'viewer';
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [nutris, setNutris] = useState([]);
  const [selectedNutri, setSelectedNutri] = useState('');
  const [annual, setAnnual] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpense, setShowExpense] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expForm, setExpForm] = useState({ category_id: '', category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], recurring: false, notes: '', attributed_to: 'clinica' });
  const [categories, setCategories] = useState([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [view, setView] = useState('mensal');
  const [filterCat, setFilterCat] = useState('');
  const [budget, setBudget] = useState(null);
  const [showBudget, setShowBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfStart, setPdfStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [pdfEnd, setPdfEnd] = useState(`${new Date().getFullYear()}-12-31`);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [bvr, setBvr] = useState(null);
  const [includeHistorical, setIncludeHistorical] = useState(false);
  const [unpaidModal, setUnpaidModal] = useState({ open: false, nutriName: '', loading: false, data: null });

  const fetch = useCallback(async () => {
    try {
      const [fRes, cRes] = await Promise.all([
        axios.get(`${API}/api/clinic-finances?month=${month}${selectedNutri ? `&nutri_id=${selectedNutri}` : ''}${includeHistorical ? '&include_historical=true' : ''}`),
        axios.get(`${API}/api/expense-categories`),
      ]);
      setData(fRes.data);
      const cats = Array.isArray(cRes.data) ? cRes.data : [];
      setCategories(cats);
    } catch {} finally { setLoading(false); }
  }, [month, selectedNutri, includeHistorical]);
  const fetchAnnual = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/annual-summary?year=${year}${includeHistorical ? '&include_historical=true' : ''}`); setAnnual(d); } catch {}
  }, [year, includeHistorical]);
  const fetchAnalytics = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/financial/analytics?year=${year}`); setAnalytics(d); } catch {}
  }, [year]);
  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/finance/billed-vs-received?month=${month}`); setBvr(data); } catch {} })();
  }, [month]);
  const openUnpaidModal = useCallback(async (nutriId, nutriName) => {
    setUnpaidModal({ open: true, nutriName, loading: true, data: null });
    try {
      const { data } = await axios.get(`${API}/api/finance/unpaid-consultations?month=${month}${nutriId ? `&nutri_id=${nutriId}` : ''}`);
      setUnpaidModal({ open: true, nutriName, loading: false, data });
    } catch {
      setUnpaidModal({ open: true, nutriName, loading: false, data: { consultations: [], total: 0 } });
      toast.error('Não foi possível carregar as consultas por pagar.');
    }
  }, [month]);
  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/staff`); setNutris(Array.isArray(data) ? data : []); } catch {} })();
  }, []);
  useEffect(() => { if (view === 'anual') fetchAnnual(); if (view === 'categoria') { fetchAnalytics(); fetchBudget(); } }, [view, fetchAnnual, fetchAnalytics]);
  const fetchBudget = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/financial/budget?year=${year}`); setBudget(d); } catch {}
  }, [year]);

  const getCatName = (e) => {
    if (e.category_id) { const c = categories.find(c => c.id === e.category_id); return c?.name || e.category || ''; }
    return e.category || '';
  };
  const getCatColor = (e) => {
    if (e.category_id) { const c = categories.find(c => c.id === e.category_id); return c?.color || '#999'; }
    return '#999';
  };

  const saveExpense = async () => {
    const payload = { ...expForm, amount: parseFloat(expForm.amount) || 0, month };
    try {
      if (editId) { await axios.put(`${API}/api/clinic-expenses/${editId}`, payload); toast.success('Despesa actualizada'); }
      else { await axios.post(`${API}/api/clinic-expenses`, payload); toast.success('Despesa adicionada'); }
      setShowExpense(false); setEditId(null); setExpForm({ category_id: '', category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], recurring: false, notes: '' }); fetch();
    } catch { toast.error('Erro'); }
  };
  const delExpense = async (id) => {
    const e = (data?.expenses || []).find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a despesa "${e?.description || getCatName(e || {}) || ''}" (${e?.amount || 0}€).` });
    if (!ok) return false;
    await axios.delete(`${API}/api/clinic-expenses/${id}`); fetch();
    return true;
  };
  const editExpense = (e) => { setEditId(e.id); setExpForm({ category_id: e.category_id || '', category: e.category || '', description: e.description || '', amount: String(e.amount || ''), date: e.date || '', recurring: e.recurring || false, notes: e.notes || '', attributed_to: e.attributed_to || 'clinica' }); setShowExpense(true); };
  const exportCsv = () => { window.open(`${API}/api/financial/expenses-csv?${view === 'mensal' ? `month=${month}` : `year=${year}`}`, '_blank'); };
  const exportPdf = () => { window.open(`${API}/api/financial/report/pdf?start_date=${pdfStart}&end_date=${pdfEnd}`, '_blank'); setShowPdfModal(false); };
  const saveBudget = async () => {
    try { await axios.post(`${API}/api/financial/budget`, { year: parseInt(year, 10), categories: budgetForm }); toast.success('Orçamento guardado'); setShowBudget(false); fetchBudget(); } catch { toast.error('Erro'); }
  };

  const filteredExpenses = (data?.expenses || []).filter(e => !filterCat || getCatName(e) === filterCat);
  // Totals by category for monthly view
  const monthCatTotals = {};
  (data?.expenses || []).forEach(e => { const n = getCatName(e); monthCatTotals[n] = (monthCatTotals[n] || 0) + (e.amount || 0); });

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="financeiro-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Financeiro</h2>
          <p className="text-xs text-brand-muted mt-0.5">Gestão financeira da clínica</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <button onClick={() => setShowExportMenu(!showExportMenu)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2 text-xs flex items-center gap-1 hover:bg-brand-bg"><FileCsv size={14} /> Exportar <CaretDown size={10} /></button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-brand-border rounded-xl shadow-lg py-1 z-10 min-w-[140px]">
                <button onClick={() => { exportCsv(); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-brand-text hover:bg-brand-bg flex items-center gap-2"><FileCsv size={14} /> Exportar CSV</button>
                <button onClick={() => { setShowPdfModal(true); setShowExportMenu(false); }} className="w-full text-left px-3 py-2 text-xs text-brand-text hover:bg-brand-bg flex items-center gap-2"><FilePdf size={14} /> Relatório PDF</button>
              </div>
            )}
          </div>
          <Link to="/admin/financeiro/categorias" className="border border-brand-border text-brand-muted rounded-full px-3 py-2 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="financeiro-categorias-link"><GearSix size={14} /> Categorias</Link>
          {view === 'mensal' && nutris.length > 0 && (
            <select value={selectedNutri} onChange={e => setSelectedNutri(e.target.value)} className="rounded-xl border border-brand-border text-sm px-3 py-2 bg-white" data-testid="financeiro-nutri-filter">
              <option value="">Toda a clínica</option>
              {nutris.map(n => <option key={n.user_id} value={n.user_id}>{n.name}</option>)}
            </select>
          )}
          {view === 'mensal' && <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border-brand-border text-sm w-auto" />}
          {(view === 'anual' || view === 'categoria') && <Input type="number" value={year} onChange={e => setYear(e.target.value)} className="rounded-xl border-brand-border text-sm w-20" min="2024" max="2030" />}
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        {['mensal', 'anual', 'categoria'].map(v => (
          <button key={v} onClick={() => setView(v)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${view === v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
            {v === 'mensal' ? 'Mensal' : v === 'anual' ? 'Anual' : 'Por Categoria'}
          </button>
        ))}
      </div>

      {/* MENSAL VIEW */}
      {view === 'mensal' && data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><TrendUp size={18} className="mx-auto text-green-600 mb-1" /><p className="text-lg font-heading font-semibold text-green-600">{data.total_revenue}€</p><p className="text-[9px] text-brand-muted">Receitas</p>{data.effective_fee_pct > 0 && <p className="text-[9px] text-brand-placeholder mt-0.5" data-testid="finance-effective-fee-pct">líq. {data.net_revenue}€ (taxa {data.effective_fee_pct}%)</p>}</div>
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><TrendDown size={18} className="mx-auto text-red-500 mb-1" /><p className="text-lg font-heading font-semibold text-red-500">{data.total_expenses}€</p><p className="text-[9px] text-brand-muted">Despesas</p></div>
            <div className={`bg-white border rounded-2xl p-4 text-center ${data.result >= 0 ? 'border-green-200' : 'border-red-200'}`}><CurrencyEur size={18} className={`mx-auto mb-1 ${data.result >= 0 ? 'text-green-600' : 'text-red-500'}`} /><p className={`text-lg font-heading font-semibold ${data.result >= 0 ? 'text-green-600' : 'text-red-500'}`}>{data.result}€</p><p className="text-[9px] text-brand-muted">Resultado</p></div>
          </div>
          {/* ITEM 1 — Faturado vs Recebido por nutricionista */}
          {bvr && bvr.rows && bvr.rows.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5" data-testid="billed-vs-received">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm text-brand-text">Faturado vs Recebido</h3>
                <div className="flex items-center gap-2">
                  {bvr.totals.stripe_fee_total > 0 && (
                    <span className="text-[10px] text-brand-muted bg-brand-bg border border-brand-border rounded-full px-2 py-1" data-testid="bvr-effective-fee" title="Taxa Stripe real, descontada do valor recebido">Taxa Stripe: {String(bvr.totals.stripe_fee_total.toFixed(2)).replace(".", ",")} €</span>
                  )}
                  {bvr.totals.pending > 0 && (
                    <button type="button" onClick={() => openUnpaidModal('', 'Todos')} className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors cursor-pointer" data-testid="bvr-total-pending">{String(bvr.totals.pending.toFixed(2)).replace(".", ",")} € por cobrar</button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {bvr.rows.map(r => (
                  <div key={r.nutri_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-brand-border/30 last:border-0" data-testid={`bvr-row-${r.nutri_id}`}>
                    <span className="text-xs text-brand-text flex-1 min-w-0 truncate">{r.name}</span>
                    <span className="text-[11px] text-brand-muted">Faturado <b className="text-brand-text">{String(r.billed.toFixed(2)).replace(".", ",")} €</b></span>
                    <span className="text-[11px] text-brand-muted">Recebido (líq.) <b className="text-green-600">{String(r.received.toFixed(2)).replace(".", ",")} €</b></span>
                    {r.pending > 0 ? (
                      <button type="button" onClick={() => openUnpaidModal(r.nutri_id, r.name)} className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex-shrink-0 hover:bg-amber-100 transition-colors cursor-pointer" data-testid={`bvr-pending-${r.nutri_id}`}>{String(r.pending.toFixed(2)).replace(".", ",")} € por cobrar</button>
                    ) : (
                      <span className="text-[11px] text-green-600 flex-shrink-0">✓ Em dia</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-brand-placeholder mt-2">Faturado = consultas registadas · Recebido = valor líquido real após taxa Stripe (ou bruto, quando não é Stripe)</p>
            </div>
          )}
          {/* Category filter + totals */}
          {Object.keys(monthCatTotals).length > 0 && (
            <div className="flex gap-1.5 mb-3 flex-wrap">
              <button onClick={() => setFilterCat('')} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${!filterCat ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Todas</button>
              {Object.entries(monthCatTotals).sort((a,b) => b[1]-a[1]).map(([cat, total]) => (
                <button key={cat} onClick={() => setFilterCat(filterCat === cat ? '' : cat)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${filterCat === cat ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{cat} ({total}€)</button>
              ))}
            </div>
          )}
          {/* Expenses list */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Despesas</h3>
              {!isViewer && <button onClick={() => { setEditId(null); setExpForm({ category_id: '', category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0], recurring: false, notes: '' }); setShowExpense(true); }} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar</button>}
            </div>
            {filteredExpenses.length === 0 ? <p className="text-xs text-brand-placeholder py-2">Sem despesas.</p> : (
              <div className="space-y-1">
                {filteredExpenses.map(e => (
                  <div key={e.id} className="flex items-center justify-between py-1.5 text-xs group rounded-lg px-1" onClick={() => { if (!isViewer) editExpense(e); }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getCatColor(e) }} />
                      <span className="text-brand-text">{e.description || getCatName(e)}</span>
                      <span className="text-[8px] bg-brand-bg text-brand-muted px-1 py-0.5 rounded">{getCatName(e)}</span>
                      {e.recurring && <span className="text-[8px] bg-brand-bg text-brand-muted px-1 py-0.5 rounded">fixo</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500 font-medium">{e.amount}€</span>
                      <button onClick={(ev) => { ev.stopPropagation(); delExpense(e.id); }} className={`${isViewer ? 'hidden' : 'hidden group-hover:block'} text-brand-placeholder hover:text-red-500`}><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ANUAL VIEW */}
      {view === 'anual' && annual && (
        <div className="bg-white border border-brand-border rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Resumo {year}</h3>
            <button onClick={() => setIncludeHistorical(v => !v)} className={`text-[11px] px-3 py-1.5 rounded-full font-medium border ${includeHistorical ? 'bg-brand-green text-white border-brand-green' : 'bg-brand-bg text-brand-muted border-brand-border'}`} data-testid="toggle-historical">
              {includeHistorical ? '✓ ' : ''}Incluir receita histórica
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-brand-border"><th className="text-left py-1.5 text-brand-muted">Mês</th><th className="text-center py-1.5 text-brand-muted">Consultas</th><th className="text-center py-1.5 text-brand-muted">Receitas</th><th className="text-center py-1.5 text-brand-muted">Despesas</th><th className="text-center py-1.5 text-brand-muted">Resultado</th></tr></thead>
              <tbody>{(annual.months || []).map(m => (<tr key={m.month} className="border-b border-brand-border/30"><td className="py-1.5 text-brand-text">{m.month}</td><td className="py-1.5 text-center">{m.consultations}</td><td className="py-1.5 text-center text-green-600">{m.revenue}€</td><td className="py-1.5 text-center text-red-500">{m.expenses}€</td><td className={`py-1.5 text-center font-medium ${m.result >= 0 ? 'text-green-600' : 'text-red-500'}`}>{m.result}€</td></tr>))}</tbody>
              <tfoot><tr className="border-t border-brand-border font-medium"><td className="py-2">Total</td><td className="py-2 text-center">{(annual.months||[]).reduce((s,m)=>s+m.consultations,0)}</td><td className="py-2 text-center text-green-600">{r2((annual.months||[]).reduce((s,m)=>s+m.revenue,0))}€</td><td className="py-2 text-center text-red-500">{r2((annual.months||[]).reduce((s,m)=>s+m.expenses,0))}€</td><td className="py-2 text-center">{r2((annual.months||[]).reduce((s,m)=>s+m.result,0))}€</td></tr></tfoot>
            </table>
          </div>
          {includeHistorical && <p className="text-[10px] text-brand-placeholder mt-2">A receita histórica está incluída nos totais. Estes valores não afetam renovação/LTV/churn por nutricionista.</p>}
          <HistoricalRevenueEditor onSaved={() => fetchAnnual()} />
        </div>
      )}

      {/* POR CATEGORIA VIEW */}
      {view === 'categoria' && analytics && (
        <div>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><p className="text-xl font-heading font-bold text-red-500">{analytics.grand_total}€</p><p className="text-[9px] text-brand-muted">Total despesas {year}</p></div>
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><p className="text-xl font-heading font-bold text-brand-text">{analytics.avg_monthly}€</p><p className="text-[9px] text-brand-muted">Média mensal</p></div>
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><p className="text-xl font-heading font-bold text-brand-green">{analytics.total_fixed}€</p><p className="text-[9px] text-brand-muted">Fixas</p></div>
            <div className="bg-white border border-brand-border rounded-2xl p-4 text-center"><p className="text-xl font-heading font-bold text-amber-600">{analytics.projection}€</p><p className="text-[9px] text-brand-muted">Projeção anual</p></div>
          </div>
          {/* Category table */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 overflow-x-auto">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Despesas por categoria</h3>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-brand-border"><th className="text-left py-1.5 text-brand-muted">Categoria</th><th className="text-right py-1.5 text-brand-muted">Total</th><th className="text-right py-1.5 text-brand-muted">Média/mês</th><th className="text-right py-1.5 text-brand-muted">% Total</th><th className="text-right py-1.5 text-brand-muted">vs Ano ant.</th></tr></thead>
              <tbody>
                {(analytics.categories || []).map(c => (
                  <tr key={c.name} className="border-b border-brand-border/30">
                    <td className="py-2 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.color }} /><span className="text-brand-text">{c.name}</span></td>
                    <td className="py-2 text-right font-medium text-brand-text">{c.total}€</td>
                    <td className="py-2 text-right text-brand-muted">{c.avg_real}€</td>
                    <td className="py-2 text-right text-brand-muted">{c.pct_of_total}%</td>
                    <td className="py-2 text-right">{c.yoy_change !== null ? <span className={c.yoy_change > 0 ? 'text-red-500' : 'text-green-600'}>{c.yoy_change > 0 ? '+' : ''}{c.yoy_change}%</span> : <span className="text-brand-placeholder">-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Monthly bars */}
          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Evolução mensal por categoria</h3>
            <div className="flex gap-1 items-end h-40">
              {Array.from({length: 12}, (_, i) => {
                const monthTotal = (analytics.categories || []).reduce((s, c) => s + (c.months?.[i] || 0), 0);
                const maxMonth = Math.max(...Array.from({length: 12}, (_, j) => (analytics.categories || []).reduce((s, c) => s + (c.months?.[j] || 0), 0)), 1);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col-reverse" style={{ height: `${(monthTotal / maxMonth) * 120}px` }}>
                      {(analytics.categories || []).map(c => {
                        const h = monthTotal > 0 ? (c.months?.[i] || 0) / monthTotal * 100 : 0;
                        return h > 0 ? <div key={c.name} style={{ height: `${h}%`, background: c.color, minHeight: 2 }} /> : null;
                      })}
                    </div>
                    <span className="text-[8px] text-brand-muted mt-1">{['J','F','M','A','M','J','J','A','S','O','N','D'][i]}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">{(analytics.categories || []).map(c => (<span key={c.name} className="flex items-center gap-1 text-[8px] text-brand-muted"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}</span>))}</div>
          </div>
        </div>
      )}

      {/* Budget vs Real (inside Por Categoria view) */}
      {view === 'categoria' && analytics && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Orçamento vs Real</h3>
            {!isViewer && <button onClick={() => { const bf = {}; (analytics.categories || []).forEach(c => { bf[c.name] = budget?.categories?.[c.name] || 0; }); setBudgetForm(bf); setShowBudget(true); }} className="text-xs text-brand-green flex items-center gap-1"><PencilSimple size={12} /> Definir Orçamento {year}</button>}
          </div>
          {!budget || !budget.categories || Object.keys(budget.categories).length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-brand-muted mb-2">Ainda não definiste um orçamento para {year}.</p>
              {!isViewer && <button onClick={() => { const bf = {}; (analytics.categories || []).forEach(c => { bf[c.name] = 0; }); setBudgetForm(bf); setShowBudget(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium">Definir Orçamento</button>}
            </div>
          ) : (
            <div className="space-y-2">
              {(analytics.categories || []).map(c => {
                const budgeted = budget.categories[c.name] || 0;
                if (!budgeted) return null;
                const pct = budgeted > 0 ? Math.round(c.total / budgeted * 100) : 0;
                const diff = budgeted - c.total;
                const barColor = pct <= 80 ? '#657161' : pct <= 100 ? '#D7BD7B' : '#C97B5B';
                return (
                  <div key={c.name} className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }} />
                    <span className="text-xs text-brand-text w-40 truncate">{c.name}</span>
                    <div className="flex-1 h-3 bg-brand-bg rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                    </div>
                    <span className="text-[10px] text-brand-muted w-10 text-right">{pct}%</span>
                    <span className={`text-[10px] w-16 text-right font-medium ${diff >= 0 ? 'text-green-600' : 'text-red-500'}`}>{diff >= 0 ? '+' : ''}{Math.round(diff)}€</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Budget modal */}
      <Dialog open={showBudget} onOpenChange={setShowBudget}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Orçamento {year}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {Object.entries(budgetForm).map(([cat, val]) => (
              <div key={cat} className="flex items-center gap-2">
                <span className="text-xs text-brand-text flex-1">{cat}</span>
                <Input type="number" value={val} onChange={e => setBudgetForm({ ...budgetForm, [cat]: parseFloat(e.target.value) || 0 })} className="w-24 rounded-xl border-brand-border text-xs" placeholder="0" />
                <span className="text-[10px] text-brand-muted">€</span>
              </div>
            ))}
            <button onClick={saveBudget} className="w-full bg-brand-green text-white rounded-full py-3 font-medium mt-3">Guardar Orçamento</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF report modal */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerar Relatório PDF</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data início</Label><Input type="date" value={pdfStart} onChange={e => setPdfStart(e.target.value)} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Data fim</Label><Input type="date" value={pdfEnd} onChange={e => setPdfEnd(e.target.value)} className="rounded-xl border-brand-border" /></div>
            </div>
            <button onClick={exportPdf} className="w-full bg-brand-green text-white rounded-full py-3 font-medium flex items-center justify-center gap-2"><FilePdf size={16} /> Gerar Relatório</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense form dialog */}
      <Dialog open={showExpense} onOpenChange={v => { if (!v) { setShowExpense(false); setEditId(null); } }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar despesa' : 'Nova despesa'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Categoria</Label>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(c => (
                  <button key={c.id || c} type="button" onClick={() => setExpForm({ ...expForm, category_id: c.id || '', category: c.name || c })}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium border ${(expForm.category_id === c.id || expForm.category === (c.name || c)) ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}>
                    {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                    {c.name || c}
                  </button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Descrição</Label><Input value={expForm.description} onChange={e => setExpForm({ ...expForm, description: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Valor (€)</Label><Input type="number" step="0.01" value={expForm.amount} onChange={e => setExpForm({ ...expForm, amount: e.target.value })} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={expForm.date} onChange={e => setExpForm({ ...expForm, date: e.target.value })} className="rounded-xl border-brand-border" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Textarea value={expForm.notes} onChange={e => setExpForm({ ...expForm, notes: e.target.value })} className="rounded-xl border-brand-border" rows={2} placeholder="Observações..." /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Atribuição</Label>
              <select value={expForm.attributed_to} onChange={e => setExpForm({ ...expForm, attributed_to: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="expense-attribution">
                <option value="clinica">Clínica geral (rateada)</option>
                {nutris.map(n => <option key={n.user_id} value={n.user_id}>{n.name}</option>)}
              </select>
              <p className="text-[10px] text-brand-placeholder mt-1">"Clínica geral" é rateada entre nutricionistas por nº de clientes ativos; as diretas contam só para a nutri escolhida.</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={expForm.recurring} onChange={e => setExpForm({ ...expForm, recurring: e.target.checked })} className="rounded" /><span className="text-xs text-brand-muted">Despesa fixa/recorrente</span></label>
            <div className="flex gap-2">
              <button onClick={saveExpense} className="flex-1 bg-brand-green text-white rounded-full py-3 font-medium">{editId ? 'Guardar' : 'Adicionar'}</button>
              {editId && <button onClick={async () => { const ok = await delExpense(editId); if (ok) { setShowExpense(false); setEditId(null); } }} className="px-4 border border-red-200 text-red-500 rounded-full py-3 text-sm">Eliminar</button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={unpaidModal.open} onOpenChange={(v) => setUnpaidModal(u => ({ ...u, open: v }))}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid="unpaid-consultations-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Consultas por pagar {unpaidModal.nutriName ? `- ${unpaidModal.nutriName}` : ''}</DialogTitle>
          </DialogHeader>
          {unpaidModal.loading ? (
            <p className="text-xs text-brand-placeholder py-4 text-center">A carregar…</p>
          ) : !unpaidModal.data || unpaidModal.data.consultations.length === 0 ? (
            <p className="text-xs text-brand-placeholder py-4 text-center" data-testid="unpaid-empty-state">Sem consultas por pagar neste mês.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {unpaidModal.data.consultations.map(c => (
                <div key={c.id} className="flex items-center justify-between gap-3 py-2 border-b border-brand-border/30 last:border-0" data-testid={`unpaid-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-brand-text truncate">{c.patient_name}</p>
                    <p className="text-[10px] text-brand-muted">{fmtDatePt(c.date)}</p>
                  </div>
                  <span className="text-xs font-medium text-amber-700 flex-shrink-0">{c.amount.toFixed(2)}€</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-brand-border">
                <span className="text-xs font-medium text-brand-text">Total</span>
                <span className="text-sm font-heading font-semibold text-amber-700" data-testid="unpaid-modal-total">{unpaidModal.data.total.toFixed(2)}€</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const MESES_ABBR = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function HistoricalRevenueEditor({ onSaved }) {
  const [hYear, setHYear] = useState('2021');
  const [months, setMonths] = useState({});
  const [years, setYears] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const loadYear = useCallback(async (y) => {
    try { const { data } = await axios.get(`${API}/api/payments/historical?year=${y}`); setMonths(data.months || {}); } catch {}
  }, []);
  const loadYears = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/payments/historical`); setYears(data.years || []); } catch {}
  }, []);
  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { if (open) loadYear(hYear); }, [hYear, open, loadYear]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { year: parseInt(hYear), months: Object.fromEntries(Object.entries(months).map(([k, v]) => [k, v === '' || v == null ? 0 : parseFloat(v)])) };
      const { data } = await axios.post(`${API}/api/payments/historical/batch`, payload);
      toast.success(`Receita histórica ${hYear} guardada: ${data.year_total}€`);
      loadYears(); onSaved && onSaved();
    } catch { toast.error('Erro ao guardar'); } finally { setSaving(false); }
  };

  const total = Object.values(months).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  return (
    <div className="mt-5 pt-4 border-t border-brand-border" data-testid="historical-revenue-editor">
      <button onClick={() => setOpen(o => !o)} className="text-xs font-medium text-brand-green flex items-center gap-1" data-testid="historical-toggle-editor">
        {open ? '▾' : '▸'} Registar receita histórica (sem paciente/consulta)
      </button>
      {years.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {years.map(y => (
            <button key={y.year} onClick={() => { setHYear(String(y.year)); setOpen(true); }} className="text-[10px] bg-brand-bg border border-brand-border rounded-full px-2 py-0.5 text-brand-muted" data-testid={`hist-year-${y.year}`}>
              {y.year}: {y.total}€
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 bg-brand-bg/50 rounded-2xl p-3">
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs text-brand-muted">Ano</label>
            <Input type="number" value={hYear} onChange={e => setHYear(e.target.value)} min="2000" max="2030" className="rounded-lg border-brand-border text-xs h-8 w-24" data-testid="hist-year-input" />
            <span className="text-xs text-brand-muted ml-auto">Total: <strong className="text-brand-text">{total.toFixed(2)}€</strong></span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {MESES_ABBR.map((nm, i) => {
              const mo = i + 1;
              return (
                <div key={mo} className="flex items-center gap-1.5">
                  <span className="text-[10px] text-brand-muted w-7">{nm}</span>
                  <Input type="number" step="0.01" value={months[mo] ?? ''} onChange={e => setMonths(m => ({ ...m, [mo]: e.target.value }))} placeholder="0" className="rounded-lg border-brand-border text-xs h-8 flex-1" data-testid={`hist-month-${mo}`} />
                </div>
              );
            })}
          </div>
          <button onClick={save} disabled={saving} className="mt-3 bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-60" data-testid="hist-save-btn">
            {saving ? 'A guardar…' : `Guardar receita ${hYear}`}
          </button>
        </div>
      )}
    </div>
  );
}
